function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { getItem, removeItem } from '../../utils/localStorage';
import { createMsalInstance, loginRequest } from '../../lib/authentication/entra/msalConfig';
import ForgotPassword from './forgotPassword';
import Loader from '../common/loader';
import SignInHeader from './signInHeader';
import VersionDisplay from '../common/infoDisplays/versionDisplay';
import Icon from '../common/containers/icon';
import ErrorDetailsToggle from '../notifications/errorDetailsToggle';
import ga from '../../utils/ga';
const handleLiveDemoClick = () => {
  ga.trackEvent('Login Screen', 'Demo Badge Pressed');
};
const LoginForm = ({
  customer,
  username,
  password,
  mfaPin,
  rememberMe,
  handleForgotPassword,
  forgotPasswordToggle,
  login,
  onChange,
  onLogin,
  ssoProviders: _ssoProviders = {},
  availableSSOProviders: _availableSSOProviders = [],
  onCustomerBlur: _onCustomerBlur = null
}) => {
  // Check for logout message on component mount
  const [logoutMessage, setLogoutMessage] = useState(null);
  // Track SSO-specific errors for inline display
  const [ssoError, setSsoError] = useState(null);
  // Track whether official Microsoft button image loaded successfully
  const [microsoftButtonImageLoaded, setMicrosoftButtonImageLoaded] = useState(true);

  // Extract Entra provider from generic SSO structure
  // Note: Currently only Entra is supported, but structure allows for multiple providers
  const entraProvider = _ssoProviders.entra || {};
  // Use availableSSOProviders as the source of truth for provider availability
  // This matches the "available providers" concept from the discovery endpoint
  const entraEnabled = _availableSSOProviders.includes('entra');
  const entraSettings = entraProvider.settings || null;
  const checkingEntra = entraProvider.checking === true;
  useEffect(() => {
    const message = getItem('logoutMessage');
    if (message) {
      setLogoutMessage(message);
      // Clear the message from localStorage so it doesn't show again
      removeItem('logoutMessage');
    }
  }, []);
  const handleCustomerChange = event => {
    let {
      target: {
        value
      }
    } = event;
    // disallow spaces in customer name
    if (value.includes(' ')) {
      value = value.replace(/\s/g, '');
    }
    onChange('customer', value);
  };
  const handleCustomerBlur = () => {
    if (_onCustomerBlur) {
      _onCustomerBlur();
    }
  };
  const handleUsernameChange = event => {
    onChange('username', event.target.value);
  };
  const handlePasswordChange = event => {
    onChange('password', event.target.value);
  };
  const handleMfaPinChange = event => {
    onChange('mfaPin', event.target.value);
  };
  const handleRememberMeChange = event => {
    onChange('rememberMe', event.target.checked);
  };
  const handleReturnKeyLogin = event => {
    // submit on return key
    if (event.keyCode === 13) {
      onLogin();
    }
  };
  const handleEntraLogin = async () => {
    // Clear any previous SSO errors
    setSsoError(null);
    if (!entraSettings) {
      setSsoError('SSO settings not available for this customer.');
      return;
    }

    // Create MSAL instance dynamically using customer settings from SSO provider
    const msalInstance = createMsalInstance(entraSettings);
    if (!msalInstance) {
      setSsoError('Failed to start sign-in. Please refresh the page.');
      return;
    }
    try {
      // Use customer from state - required for Entra login
      const customerToUse = customer;

      // Store customer settings in sessionStorage for callback handler
      // Check for sessionStorage availability (may not be available in private browsing mode)

      if (typeof sessionStorage !== 'undefined' && sessionStorage) {
        try {
          sessionStorage.removeItem('entra_callback_processed');
          // Store customer settings so callback can create MSAL instance

          sessionStorage.setItem('entra_customer_settings', JSON.stringify(entraSettings));
        } catch (storageError) {
          // SessionStorage may be disabled or quota exceeded - log warning but continue
          // eslint-disable-next-line no-console
          console.warn('Failed to store SSO settings in sessionStorage:', storageError);
        }
      }

      // Ensure MSAL is initialized first - always call initialize()
      // MSAL handles multiple initialize() calls gracefully
      await msalInstance.initialize();

      // Handle any pending redirects first to clear interaction state
      // This prevents "interaction_in_progress" errors
      try {
        const redirectResponse = await msalInstance.handleRedirectPromise();
        if (redirectResponse) {
          // If there's a pending redirect, let the callback handler process it
          // Redirect to callback route

          if (typeof window !== 'undefined') {
            window.location.href = '/auth/callback.html';
          }
          return;
        }
      } catch (_unused) {
        // Continue with login redirect even if handleRedirectPromise fails
        // This is OK if no redirect is pending
      }

      // Initiate login redirect with PKCE
      // Note: loginRedirect will cause a full page redirect to Microsoft
      // The customer is passed in the state parameter, which MSAL will return in the response
      await msalInstance.loginRedirect(_objectSpread(_objectSpread({}, loginRequest), {}, {
        // Include customer in state - MSAL will return this in the response
        state: customerToUse
      }));
    } catch (error) {
      // Show error to user inline
      setSsoError(`Login failed: ${error.message || error}`);
    }
  };

  // login form display
  return /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement(Row, {
    style: {
      margin: 25
    }
  }, /*#__PURE__*/React.createElement(Col, {
    sm: {
      size: 4,
      offset: 4
    }
  }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading"
  }, /*#__PURE__*/React.createElement(SignInHeader, null)), /*#__PURE__*/React.createElement(CardBody, null, /*#__PURE__*/React.createElement("form", {
    onSubmit: e => e.preventDefault()
  }, !login.mfa ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    id: "customer",
    autoComplete: "off",
    autoCapitalize: "off",
    type: "text",
    value: customer,
    placeholder: "Customer name",
    onChange: handleCustomerChange,
    onBlur: handleCustomerBlur,
    className: "t-lf-customer-name tqc-control"
  })), !entraEnabled && /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    id: "username",
    autoComplete: "off",
    autoCapitalize: "off",
    type: "text",
    value: username,
    placeholder: "Username",
    onChange: handleUsernameChange,
    className: "t-lf-username tqc-control"
  }))) : undefined, !login.mfa && !entraEnabled ? /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    id: "password",
    autoComplete: "off",
    autoCapitalize: "off",
    type: "password",
    placeholder: "Password",
    value: password,
    onKeyDown: handleReturnKeyLogin,
    onChange: handlePasswordChange,
    className: "t-lf-password tqc-control"
  })) : undefined, login.mfa ? /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    id: "totp",
    autoComplete: "one-time-code",
    autoFocus: true,
    autoCapitalize: "off",
    type: "text",
    value: mfaPin,
    onKeyDown: handleReturnKeyLogin,
    placeholder: "Authenticator PIN",
    onChange: handleMfaPinChange,
    className: "t-lf-mfa tqc-control",
    style: {
      margin: '50px 0px 65px 0'
    }
  })) : undefined, !entraEnabled && /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    xs: 6
  }, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    id: "loginButton",
    onClick: onLogin,
    disabled: !login.loaded,
    className: "t-lf-login"
  }, "Login")), !login.mfa && /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    style: {
      textAlign: 'right',
      paddingTop: '8px'
    }
  }, /*#__PURE__*/React.createElement(Label, {
    check: true,
    htmlFor: "rememberMe",
    style: {
      margin: 0,
      marginRight: '20px'
    }
  }, "Remember me"), /*#__PURE__*/React.createElement(Input, {
    id: "rememberMe",
    type: "checkbox",
    checked: rememberMe,
    onChange: handleRememberMeChange,
    style: {
      marginLeft: '-13px'
    }
  }))), !login.mfa && customer && entraEnabled && !checkingEntra && /*#__PURE__*/React.createElement(Row, {
    style: {
      marginTop: '15px'
    }
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleEntraLogin,
    disabled: !login.loaded,
    className: "t-lf-entra-login"
  }, microsoftButtonImageLoaded && /*#__PURE__*/React.createElement("img", {
    src: "/img/microsoft/ms-symbollockup_signin_light.svg",
    alt: "Sign in with Microsoft",
    onError: () => {
      setMicrosoftButtonImageLoaded(false);
    },
    onLoad: () => {
      setMicrosoftButtonImageLoaded(prev => prev === false ? true : prev);
    }
  }), !microsoftButtonImageLoaded && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("svg", {
    width: "21",
    height: "21",
    viewBox: "0 0 21 21",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0H10V10H0V0Z",
    fill: "#F25022"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11 0H21V10H11V0Z",
    fill: "#7FBA00"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M0 11H10V21H0V11Z",
    fill: "#00A4EF"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11 11H21V21H11V11Z",
    fill: "#FFB900"
  })), "Sign in with Microsoft")))), !login.mfa && customer && checkingEntra && /*#__PURE__*/React.createElement(Row, {
    style: {
      marginTop: '15px'
    }
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: 'center',
      padding: '10px'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      color: '#666'
    }
  }, "Checking sign-in options...")))), /*#__PURE__*/React.createElement(Loader, {
    loaded: login.loaded
  })), login.error || logoutMessage || ssoError ? /*#__PURE__*/React.createElement(Alert, {
    className: "t-lg-alter-error t-error-details-host",
    style: {
      marginTop: '15px',
      marginBottom: '0px'
    },
    color: login.error || ssoError ? 'danger' : 'warning'
  }, login.error || logoutMessage || ssoError, login.errorHint || login.errorTechnicalDetail || login.errorReference || login.errorCode ? /*#__PURE__*/React.createElement(ErrorDetailsToggle, {
    hint: login.errorHint || '',
    technicalDetail: login.errorTechnicalDetail || '',
    reference: login.errorReference || '',
    errorCode: login.errorCode || ''
  }) : '') : undefined)), !entraEnabled && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("button", {
    id: "forgotPasswordLink",
    className: "btn-link",
    onClick: forgotPasswordToggle,
    type: "button"
  }, "Forgot password?"), login.showForgotPassword ? /*#__PURE__*/React.createElement(ForgotPassword, {
    handleForgotPassword: handleForgotPassword,
    onHide: forgotPasswordToggle
  }) : undefined), /*#__PURE__*/React.createElement("div", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement("h3", {
    style: {
      marginTop: '25px'
    }
  }, /*#__PURE__*/React.createElement("span", null, "Don't have an account?", /*#__PURE__*/React.createElement("br", null), "Register now to demo TRU.")), /*#__PURE__*/React.createElement(Button, {
    className: "t-lg-request-consultation",
    size: "large",
    href: "https://www.trusolutions.com/demo-tru/",
    target: "_blank",
    onClick: handleLiveDemoClick
  }, "Request a Consultation ", /*#__PURE__*/React.createElement(Icon, {
    glyph: "angle-right"
  })), /*#__PURE__*/React.createElement(VersionDisplay, {
    style: {
      paddingTop: '25px'
    }
  })))));
};
LoginForm.propTypes = {
  customer: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  mfaPin: PropTypes.string.isRequired,
  rememberMe: PropTypes.bool.isRequired,
  login: PropTypes.shape({
    error: PropTypes.string,
    errorHint: PropTypes.string,
    errorTechnicalDetail: PropTypes.string,
    errorReference: PropTypes.string,
    errorCode: PropTypes.string,
    mfa: PropTypes.bool,
    loaded: PropTypes.bool,
    showForgotPassword: PropTypes.bool.isRequired
  }).isRequired,
  forgotPasswordToggle: PropTypes.func.isRequired,
  handleForgotPassword: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  ssoProviders: PropTypes.objectOf(PropTypes.shape({
    enabled: PropTypes.bool,
    loginUrl: PropTypes.string,
    checking: PropTypes.bool,
    settings: PropTypes.shape({
      tenantId: PropTypes.string,
      clientId: PropTypes.string,
      authority: PropTypes.string,
      redirectUri: PropTypes.string
    })
  })),
  availableSSOProviders: PropTypes.arrayOf(PropTypes.string),
  onCustomerBlur: PropTypes.func
};
export default LoginForm;